import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { AuthenticationService } from 'core/authentication/authentication.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { SignalRService } from 'core/signalR-service/signal-r.service';
import { environment } from '../../../environments/environment';
import { SignalrConnectionType } from './entities/signalrConnectionType.enum';
import { SignalrMethodName } from './entities/signalrMethodName.enum';
import { SignalrNotification } from './entities/SignalrNotification.class';
import { SignalrProgressState } from './entities/signalrProgressState.enum';
import { GoOfflineSyncInProgressScreenComponent } from './go-offline-sync-in-progress-screen/go-offline-sync-in-progress-screen.component';
import { OfflineNoInternetConnectionComponent } from './offline-internet-connection-required/offline-no-internet-connection.component';
import { SyncStepperComponent } from './sync-stepper/sync-stepper.component';
import { AppLocalization } from './entities/app-localization';

@Injectable({
  providedIn: 'root'
})

export class OfflineService implements OnDestroy {
  private appStrings: AppLocalization;
  private firstSyncKey = 'firstSyncDone';
  private offlineConnectionReqSize = {
    width: '99vw',
    height: '90vh'
  };
  private urls = {
    startGoOnlineSync: '/api/client/goOnline',
    getOfflineTimerStatus: '/api/appsettings/offlinetimer',
    startOfflineTimer: '/api/appsettings/goOffline',
    receiveOnlineUpdates: '/api/client/receiveOnlineUpdates',
    detectOfflineSyncUpdates: '/api/client/detectOfflineSyncUpdates'
  };
  private goOnlineSyncCompWidth = '75vw';
  private goOfflineSyncCompWidth = '95vw';
  private subs = new Subscription();
  private receiveOnlineUpdatesOnDemand = false;

  isSyncStatusBusy = true; // used to avoid hammering the Go Offline button
  triggerStartGoOnlineSync$ = new EventEmitter<any>();
  syncStatuses = {
    failed: 'Failed',
    inProgress: 'In Progress'
  };

  constructor(
    private appInfoService: AppInfoService,
    private apiService: ApiService,
    private appStateService: AppStateService,
    private signalrService: SignalRService,
    private router: Router,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private authentificationService: AuthenticationService
  ) {
    this.appStrings = this.appInfoService.appStrings;

    this.subs.add(this.triggerStartGoOnlineSync$.subscribe(goOnlineTriggered => {
      if (goOnlineTriggered) {
        this.startGoOnlineSync();
      }
    }));
    this.subs.add(this.appStateService.offlineStateToggle$.subscribe(isOnline => {
      if (isOnline === false) {
        this.startOfflineTimer();
      }
    }));

    this.subs.add(this.signalrService.startedGoOnlineSyncProgressReceived$
      .subscribe(progress => this.handleGoOnlineSyncProgress(progress)));

    this.subs.add(this.signalrService.receiveOnlineUpdatesProgressReceived$
      .subscribe(progress => this.handleReceiveOnlineUpdatesProgress(progress)));

    this.subs.add(this.authentificationService.offlineAuthenticationDone$.subscribe(() => {
      if (!this.appInfoService.userData.UserInfo.EmployeeId) {
        this.appInfoService.initializeUserData().subscribe(userInfo => {
          this.appInfoService.userData = this.appInfoService.parseAccessAndModules(userInfo);
        });
      }
      this.getOfflineTimerStatus().subscribe(syncResult => this.appStateService.setOfflineSyncStatus(syncResult));
    }
    ));
  }

  /**
   * Syncs the offline timer with the server
   */
  getOfflineTimerStatus() {
    return this.apiService.getGeneric(environment.offlineApiRoot + this.urls.getOfflineTimerStatus, true);
  }

  /**
   * Starts the offline timer on the Back-End
   */
  private startOfflineTimer() {
    this.apiService.postGeneric(environment.offlineApiRoot + this.urls.startOfflineTimer, null, true)
      .subscribe(syncResult => this.appStateService.setOfflineSyncStatus(syncResult));
  }

  /**
   * Displays a modal to warn the user that the app needs to be synced
   */
  displayNoInternetConnectionModal() {
    this.dialog.open(OfflineNoInternetConnectionComponent, {
      disableClose: true,
      width: this.offlineConnectionReqSize.width,
      height: this.offlineConnectionReqSize.height,
      maxWidth: this.offlineConnectionReqSize.width
    });
  }

  /**
   * Initiates the sync required when the user goes in online mode from offline mode
   */
  startGoOnlineSync() {
    if (!this.appStateService.state.isOnline) {
      this.appStateService.goOnline();
    }
    this.appStateService.triggerIsLoading$.next(true);
    this.signalrService.startConnection(SignalrConnectionType.StartGoOnlineSync,
      false, `${environment.offlineApiRoot}${environment.signalR}`)
      .then(() => {
        const signalrNotification = new SignalrNotification(
          this.signalrService.connectionId,
          null,
          SignalrMethodName.startGoOnlineSync
        );
        const requestObj = {
          id: this.appInfoService.userData.UserInfo.EmployeeId,
          notification: signalrNotification,
          token: this.authentificationService.getToken()
        };
        this.apiService.postGeneric(environment.offlineApiRoot + this.urls.startGoOnlineSync, requestObj, true).subscribe(() => {
          this.appStateService.triggerIsLoading$.next(false);
          this.openStateToggledSyncDialog(true);
        }, () => {
          this.appStateService.triggerIsLoading$.next(false);
        });
      });
  }

  /**
   * Triggers the receiving of online updates
   */
  startReceivingOnlineUpdates(onDemand?) {
    this.receiveOnlineUpdatesOnDemand = onDemand;
    this.signalrService.startConnection(SignalrConnectionType.ReceiveOnlineUpdates,
      false, `${environment.offlineApiRoot}${environment.signalR}`)
      .then(() => {
        const signalrNotification = new SignalrNotification(
          this.signalrService.connectionId,
          this.appInfoService.userData.UserInfo.EmployeeId,
          SignalrMethodName.receiveOnlineUpdates
        );

        const reqObj = {
          id: this.appInfoService.userData.UserInfo.EmployeeId,
          syncOnDemand: onDemand,
          notification: signalrNotification,
          token: this.authentificationService.getToken()
        };

        if (!onDemand) {
          delete reqObj.syncOnDemand;
        }

        this.apiService.postGeneric(environment.offlineApiRoot + this.urls.receiveOnlineUpdates, reqObj, true).subscribe(() => {
          // trigger flag with sync in progress
          this.appStateService.receiveOnlineUpdatesInProgress = true;
          this.isSyncStatusBusy = false;
          if (onDemand) {
            this.openStateToggledSyncDialog(false);
          }
        }, () => {
          this.handleErrorForFailedReceiveOnlineUpdates();
        });
      });
  }

  private handleReceiveOnlineUpdatesProgress(progress) {
    if (progress.state === SignalrProgressState.Done) {
      this.appStateService.receiveOnlineUpdatesInProgress = false;
      this.appStateService.receiveOnlineUpdatesDone$.emit(true);
      if (!window.localStorage.getItem(this.firstSyncKey)) {
        window.localStorage.setItem(this.firstSyncKey, 'true');
        this.notificationService.showNotification(this.appStrings.onlineSync_firstSyncDone, NotificationType.Success);
      }
      this.signalrService.closeConnection();
    }

    if (progress.state === SignalrProgressState.Error) {
      this.handleErrorForFailedReceiveOnlineUpdates();
      this.signalrService.closeConnection();
    }
  }

  /**
   * Handles the error for failed last synchronization status get
   */
  private handleErrorForFailedReceiveOnlineUpdates() {
    this.isSyncStatusBusy = false;
    this.appStateService.receiveOnlineUpdatesInProgress = false;
    this.notificationService.showNotification(
      this.appStrings.onlineSync_failureMessage,
      NotificationType.Error,
      false,
      false,
      () => this.router.navigate(['/applicationStatus', 'errorList'])
    );
  }

  private handleGoOnlineSyncProgress(progress) {
    if (progress.state === SignalrProgressState.Done) {
      window.localStorage.removeItem(this.appStateService.shouldSyncKey);
      this.signalrService.closeConnection();
    }

    if (progress.state === SignalrProgressState.Error) {
      this.signalrService.closeConnection();
    }
  }

  /**
   * Opens the dialog with go online sync status
   * @param forGoOnline - is opened for go online
   */
  private openStateToggledSyncDialog(forGoOnline: boolean) {
    let dialogWidth;
    if (forGoOnline) {
      dialogWidth = this.goOnlineSyncCompWidth;
    } else {
      dialogWidth = this.goOfflineSyncCompWidth;
    }
    const dialogRef = this.dialog.open(SyncStepperComponent, {
      data: {
        goOffline: !forGoOnline,
        goOnline: forGoOnline
      },
      disableClose: true,
      width: dialogWidth,
      maxWidth: dialogWidth
    });

    dialogRef.beforeClosed().subscribe(res => {
      if (res === 1) { // retry
        this.stateToggleSyncConfirmed(forGoOnline);
      } else {
        if (res === 2) { // cancel
          if (forGoOnline) {
            this.handleCancelGoOnline();
          }
        }
      }
    });
  }

  /**
   * Start go online or start receiving online updates
   * @param forGoOnline is confirmed for go online
   */
  stateToggleSyncConfirmed(forGoOnline: boolean) {
    if (forGoOnline) {
      window.localStorage.removeItem(this.appStateService.shouldSyncKey);
      this.startGoOnlineSync();
    } else {
      this.startReceivingOnlineUpdates(this.receiveOnlineUpdatesOnDemand);
    }
  }

  /**
   * Loads a dialog that informs the user a sync process is in progress
   */
  displayGoOfflineSyncInProgressScreen() {
    this.dialog.open(GoOfflineSyncInProgressScreenComponent, {
      disableClose: true,
      width: '70vw'
    });
  }

  /**
   * Detects if any changes made in offline mode
   * @returns as {Observable<any>}
   */
  detectOfflineSyncUpdates() {
    return this.apiService.get(this.urls.detectOfflineSyncUpdates);
  }

  /**
   * Handles the case when goOnline sync fails, and user choose CANCEL option from dialog
   */
  private handleCancelGoOnline() {
    window.localStorage.removeItem(this.appStateService.shouldSyncKey);
    // set the appState back to offline mode
    this.appStateService.goOffline();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
